// `<` `>` `"` never appear unencoded in a URL, so keeping them out of the match
// is what stops a pasted `<a href="…">` or JSON blob running into the link.
// Schemes are case-insensitive.
const URL_PATTERN = /https?:\/\/[^\s<>"]+/gi;
const TRAILING_PUNCTUATION = /[.,;:!?']+$/;
const CLOSE_TO_OPEN: Record<string, string> = { ")": "(", "]": "[", "}": "{" };
const countOf = (text: string, char: string): number => text.split(char).length - 1;

// Trims what the surrounding prose left on the end of a pasted URL: sentence
// punctuation, and any closing bracket the URL never opened — `(see
// https://example.com/a_(b))` ends with one paren that closes the link and one
// that closes the prose. Counting brackets, not testing for one's presence, is
// what lets a URL keep its own parens. Repeats until neither applies, so
// `.../doc.)` loses both.
const trimTrailingPunctuation = (url: string): string => {
  let trimmed = url;
  for (;;) {
    const punctuation = trimmed.match(TRAILING_PUNCTUATION);
    if (punctuation) {
      trimmed = trimmed.slice(0, -punctuation[0].length);
      continue;
    }
    const last = trimmed.at(-1) ?? "";
    const open = CLOSE_TO_OPEN[last];
    if (!open || countOf(trimmed, open) >= countOf(trimmed, last)) return trimmed;
    trimmed = trimmed.slice(0, -1);
  }
};

// Only http(s) URLs become links: that keeps false positives out and is also the
// guard that stops javascript:/data: text from ever becoming a live href.
export const linkifyText = (text: string): Array<{ type: "text" | "link"; value: string }> => {
  const matches = text.matchAll(URL_PATTERN);
  const segments: Array<{ type: "text" | "link"; value: string }> = [];
  let cursor = 0;

  for (const match of matches) {
    const start = match.index;
    const raw = match[0];
    const trimmed = trimTrailingPunctuation(raw);
    if (start > cursor) segments.push({ type: "text", value: text.slice(cursor, start) });
    segments.push({ type: "link", value: trimmed });
    cursor = start + trimmed.length;
  }

  if (cursor < text.length) segments.push({ type: "text", value: text.slice(cursor) });

  return segments.length > 0 ? segments : [{ type: "text", value: text }];
};
